import { Injectable, computed, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';

export interface SidebarItem {
  label: string;
  icon: string;
  route?: string;
  activeRoutes?: string[];
  children?: SidebarItem[];
  expanded?: boolean;
}

export interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

/**
 * Single source of truth for the sidebar nav tree — shared by the sidebar
 * itself and the main-card header, which derives its breadcrumb (section /
 * page) from the same data so the two never drift apart.
 */
@Injectable({
  providedIn: 'root',
})
export class NavSectionsService {
  private router = inject(Router);
  private currentOrgStore = inject(CurrentOrganizationStore);
  private destroyRef = inject(DestroyRef);

  private currentUrl = signal(this.router.url);

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.currentUrl.set(this.router.url));
  }

  isOnUserPage = computed(() => this.currentUrl().startsWith('/user'));

  sections = computed((): SidebarSection[] => {
    const currentOrg = this.currentOrgStore.organization();
    const currentUrl = this.currentUrl().split(/[?#]/)[0];

    if (currentUrl.startsWith('/user')) {
      return [
        {
          title: 'Benutzer',
          items: [
            {
              label: 'Kontoeinstellungen',
              icon: 'lucideSettings',
              route: '/user/settings',
            },
          ],
        },
      ];
    }

    const orgSlug = currentOrg?.slug;
    if (!orgSlug) {
      return [];
    }

    return [
      {
        title: 'Allgemein',
        items: [
          {
            label: 'Übersicht',
            icon: 'lucideLayoutDashboard',
            route: `/organizations/${orgSlug}/dashboard`,
          },
          {
            label: 'Kampagnen',
            icon: 'lucideRocket',
            route: `/organizations/${orgSlug}/campaigns`,
          },
          {
            label: 'Adressen',
            icon: 'lucideMapPin',
            route: `/organizations/${orgSlug}/database/addresses`,
          },
          {
            label: 'Analysen',
            icon: 'lucideChartLine',
            route: `/organizations/${orgSlug}/database/analyses`,
            activeRoutes: [`/organizations/${orgSlug}/analyses`],
          },
          {
            label: 'Plugin',
            icon: 'lucideCode',
            route: `/organizations/${orgSlug}/plugin`,
          },
        ],
      },
      {
        title: 'Katalog',
        items: [
          {
            label: 'Vorlagen',
            icon: 'lucideFileCog',
            route: `/organizations/${orgSlug}/catalog/templates`,
          },
          {
            label: 'Module',
            icon: 'lucideSun',
            route: `/organizations/${orgSlug}/catalog/equipment/modules`,
          },
          {
            label: 'Wechselrichter',
            icon: 'lucideZap',
            route: `/organizations/${orgSlug}/catalog/equipment/inverters`,
          },
          {
            label: 'Speicher',
            icon: 'lucideBatteryCharging',
            route: `/organizations/${orgSlug}/catalog/equipment/storages`,
          },
        ],
      },
      {
        title: 'Organisation',
        items: [
          {
            label: 'Einstellungen',
            icon: 'lucideSettings',
            route: `/organizations/${orgSlug}/settings`,
          },
        ],
      },
    ];
  });

  /** The section + item whose route is the longest prefix match of the current URL. */
  activeBreadcrumb = computed((): { section: string; page: string } | null => {
    const currentUrl = this.currentUrl().split(/[?#]/)[0];
    let best: { section: string; page: string; routeLength: number } | null = null;

    for (const section of this.sections()) {
      for (const item of section.items) {
        for (const route of [item.route, ...(item.activeRoutes ?? [])]) {
          if (route && (currentUrl === route || currentUrl.startsWith(route + '/'))) {
            if (!best || route.length > best.routeLength) {
              best = { section: section.title, page: item.label, routeLength: route.length };
            }
          }
        }
      }
    }

    return best ? { section: best.section, page: best.page } : null;
  });
}
